import React, { useState, useEffect, useRef } from 'react';
import ReactPagination from 'react-js-pagination';
import { useSelector, useDispatch } from 'react-redux';

// actions
import { loadAgents } from '../../actions/agent';

// components
import { ModalBase, Table, LoaderButton } from '../../common/components';

function SelectedAgentModal({
  title = 'Select an Agent',
  helpText = '',
  modalName = 'selectAgentModal',
  handleAgentSelect = () => {},
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  // -------- local state ----------
  const [ searchTerm, setSearchTerm ] = useState();
  const [ loading, setLoading ] = useState(false);
  const [ activePage, setActivePage ] = useState();
  const [ pageSize, setPageSize ] = useState();
  const [ totalPages, setTotalPages ] = useState();
  const [ totalAgents, setTotalAgents ] = useState();
  const [ agents, setAgents ] = useState([]);
  const [ errorMessage, setErrorMessage ] = useState('');

  // -------- redux stuff ----------
  const { [modalName]: modalOpen } = useSelector(store => store.modals);

  // -------- modal stuff ----------
  function closeModalAction() {
    dispatch({ type: 'CLOSE_MODAL', modal: modalName });
  }

  // -------- api stuff ----------
  function handleLoadAgents(pageNum) {
    setLoading(true);
    loadAgents({
      page: pageNum,
      search_term: searchTerm,
    })
      .then(data => {
        setPageSize(data.MaxPageSize);
        setAgents(data.Users);
        setActivePage(data.Page);
        setTotalPages(data.TotalPages);
        setTotalAgents(data.TotalUsers);
      })
      .catch(err => setErrorMessage(err))
      .finally(() => setLoading(false));
  }

  // -------- form stuff ----------
  const formatNameCell = (data) => `${data.first_name}${data.last_name ? ` ${data.last_name}` : ''}`;
  const formatSelectCell = (data) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={() => {
        handleAgentSelect(data);
        setSearchTerm('');
        setAgents([]);
        closeModalAction();
      }}
    >
      Select
    </button>
  );
  const schema = {
    cols: [
      {
        header: 'Name',
        passAllData: true,
        formatter: formatNameCell,
      },
      {
        header: 'Email',
        field: 'email',
      },
      {
        header: 'NPN',
        field: 'npn',
      },
      {
        header: '',
        passAllData: true,
        formatter: formatSelectCell,
      },
    ],
  };

  // -------- pagination stuff ----------
  const pageCountStart = ((activePage - 1) * pageSize) + 1;
  let pageCountEnd = activePage * pageSize;
  if (totalPages === activePage) pageCountEnd = totalAgents;

  // -------- render stuff ----------
  useEffect(() => {
    if (modalOpen && inputRef.current) setTimeout(() => inputRef.current.focus(), 200);
  }, [modalOpen]);

  return (
    <ModalBase
      isOpen={modalOpen}
      closeModalAction={() => closeModalAction()}
      staticWidth="80%"
      allowOverlayClickClose
    >
      <h3 className="mb-4">{title}</h3>

      <div className="row justify-content-between">
        <div className="col-md-4">
          <div className="input-group">
            <input
              ref={inputRef}
              className="form-control"
              placeholder="Name / Email / NPN"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleLoadAgents(1);
              }}
            />
            <LoaderButton
              text="Search"
              loading={loading}
              loadingText="Searching"
              className="btn btn-outline-secondary"
              onClick={() => handleLoadAgents(1)}
            />
          </div>
        </div>

        <div className="col-md-4">
          <p className="text-muted fs-5">
            {helpText}
          </p>
        </div>
      </div>

      <hr className="my-4" />

      <div className="row">
        <div className="col">
          <div className="overflow-scroll">
            <Table
              className="table table-striped"
              schema={schema}
              data={agents}
              loading={loading}
              errorMessage={errorMessage}
            />
          </div>
        </div>
      </div>

      <div className="d-flex flex-row m-3 position-relative">
        <div className="d-flex ms-0 position-absolute">
          <span className="text-muted">
            <i>{`${pageCountStart}-${pageCountEnd} of ${totalAgents} agents`}</i>
          </span>
        </div>
        <ReactPagination
          activePage={activePage}
          itemsCountPerPage={pageSize}
          totalItemsCount={totalAgents || 0}
          pageRangeDisplayed={5}
          innerClass="pagination mb-0 ms-auto me-auto"
          itemClass="page-item"
          linkClass="page-link"
          onChange={(pageNum) => handleLoadAgents(pageNum)}
        />
      </div>
    </ModalBase>
  );
}

export default SelectedAgentModal;
